import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ListChecks, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useApiSWR } from "@/hooks/useApiSWR";
import { listProjects, type Project } from "@/services/project.service";
import {
	type ChecklistTemplate,
	listChecklistTemplates,
	createChecklistTemplate,
} from "@/services/task-checklist.service";
import { TemplateCard } from "@/components/settings/checklist-templates/TemplateCard";
import { TemplateEditor } from "@/components/settings/checklist-templates/TemplateEditor";

export default function ChecklistTemplatesPage() {
	const [searchParams, setSearchParams] = useSearchParams();
	const projectId = searchParams.get("project") ?? "";
	const [creating, setCreating] = useState(false);

	const { data: projects, isLoading: projectsLoading } = useApiSWR<Project[]>(
		["projects", "for-checklist-templates"],
		() => listProjects(),
	);

	const {
		data: templates,
		isLoading,
		error,
		mutate,
	} = useApiSWR<ChecklistTemplate[]>(
		projectId ? ["checklist-templates", projectId] : null,
		() => listChecklistTemplates(projectId),
	);

	const { defaults, projectTemplates } = useMemo(() => {
		const all = templates ?? [];
		return {
			defaults: all.filter((t) => t.project_id === null),
			projectTemplates: all.filter((t) => t.project_id !== null),
		};
	}, [templates]);

	function selectProject(id: string) {
		const next = new URLSearchParams(searchParams);
		if (id) next.set("project", id);
		else next.delete("project");
		setSearchParams(next);
		setCreating(false);
	}

	return (
		<div className="max-w-4xl mx-auto px-6 py-8">
			<div className="flex items-center gap-2.5 mb-1">
				<ListChecks className="h-5 w-5 text-primary" />
				<h1 className="text-lg font-semibold text-foreground">
					Checklist Templates
				</h1>
			</div>
			<p className="text-sm text-muted mb-6">
				Create reusable checklists your team can apply to any task.
			</p>

			{/* Project selector */}
			<div className="mb-6">
				<label className="block text-xs font-medium text-muted mb-1.5">
					Project
				</label>
				<select
					value={projectId}
					disabled={projectsLoading}
					onChange={(e) => selectProject(e.target.value)}
					className="h-9 w-full max-w-sm rounded-lg border border-border-strong bg-surface px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
				>
					<option value="">
						{projectsLoading ? "Loading projects..." : "Select a project"}
					</option>
					{(projects ?? []).map((p) => (
						<option key={p.id} value={p.id}>
							{p.name}
						</option>
					))}
				</select>
			</div>

			{!projectId ? (
				<div className="rounded-lg border border-dashed border-border px-4 py-10 text-center text-sm text-muted">
					Select a project to manage its checklist templates.
				</div>
			) : isLoading ? (
				<div className="flex items-center gap-2 text-sm text-muted py-8">
					<Loader2 className="h-4 w-4 animate-spin" />
					Loading templates...
				</div>
			) : error ? (
				<div className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-danger">
					Failed to load templates.
				</div>
			) : (
				<div className="space-y-8">
					{/* Project templates */}
					<section>
						<div className="flex items-center justify-between mb-3">
							<h2 className="text-sm font-semibold text-foreground">
								Project templates
							</h2>
							{!creating && (
								<Button
									size="sm"
									onClick={() => setCreating(true)}
								>
									<Plus className="h-3.5 w-3.5 mr-1.5" />
									New template
								</Button>
							)}
						</div>

						{creating && (
							<div className="rounded-lg border border-border bg-surface p-4 mb-3">
								<TemplateEditor
									submitLabel="Create template"
									onCancel={() => setCreating(false)}
									onSubmit={async (name, items) => {
										await createChecklistTemplate(
											projectId,
											name,
											items,
										);
										toast.success("Template created");
										setCreating(false);
										await mutate();
									}}
								/>
							</div>
						)}

						{projectTemplates.length === 0 && !creating ? (
							<div className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-sm text-muted">
								No project templates yet. Create one or duplicate a
								default below.
							</div>
						) : (
							<div className="space-y-3">
								{projectTemplates.map((t) => (
									<TemplateCard
										key={t.id}
										projectId={projectId}
										template={t}
										onChanged={mutate}
									/>
								))}
							</div>
						)}
					</section>

					{/* Default templates */}
					{defaults.length > 0 && (
						<section>
							<h2 className="text-sm font-semibold text-foreground mb-1">
								Default templates
							</h2>
							<p className="text-xs text-muted mb-3">
								Built-in templates are read-only. Duplicate one to
								customize it for this project.
							</p>
							<div className="space-y-3">
								{defaults.map((t) => (
									<TemplateCard
										key={t.id}
										projectId={projectId}
										template={t}
										onChanged={mutate}
									/>
								))}
							</div>
						</section>
					)}
				</div>
			)}
		</div>
	);
}
